import { useMemo, type ReactNode } from "react";
import { type Column, type SortState } from "@fawkes/ui";
import { WeergaveKeuze, SoortTegels, type Weergave, type SoortTegel } from "./CatalogusKop";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Eén lijst in drie weergaven, gevoed door dezelfde kolomdefinities.
 *
 * <p>De kaarten worden opgebouwd uit de `Column`-lijst die de tabel toch al heeft: elke kolom wordt
 * een label met zijn waarde. Zo hoeft geen enkel scherm een tweede opmaak bij te houden die na de
 * eerste kolomwijziging alweer uit de pas loopt — en zo zien de tarieven er hetzelfde uit als de
 * catalogus.
 *
 * <p>De eerste kolom is de kop van de kaart (dat is overal de naam waar de regel over gaat); een
 * kolom zonder kop (de actiekolom) zakt naar de voet.
 */
export function CatalogusLijst<T>({ columns, rows, getRowKey, empty, weergave, onRowDoubleClick, sort, onSortChange }: {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (rij: T) => string;
  empty: string;
  weergave: Weergave;
  onRowDoubleClick?: (rij: T) => void;
  /** Sorteren blijft een tabelding; in kaartweergave is er geen kolomkop om op te klikken. */
  sort?: SortState;
  onSortChange?: (volgende: SortState) => void;
}) {
  const { kop, feiten, acties } = useMemo(() => ({
    kop: columns[0],
    feiten: columns.slice(1).filter((c) => c.header !== ""),
    acties: columns.filter((c) => c.header === ""),
  }), [columns]);

  if (weergave === "lijst") {
    return (
      <IndeelbareTabel tabelKey="catalogus-lijst"
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        empty={empty}
        onRowDoubleClick={onRowDoubleClick}
        sort={sort}
        onSortChange={onSortChange}
      />
    );
  }

  if (rows.length === 0) {
    return <p className="fawkes-muted">{empty}</p>;
  }

  return (
    <div className={`fawkes-catalogus__kaarten${weergave === "compact" ? " fawkes-catalogus__kaarten--compact" : ""}`}>
      {rows.map((rij) => (
        <article className="fawkes-catalogus__kaart" key={getRowKey(rij)}>
          <div className="fawkes-catalogus__kaart-kop">
            <span className="fawkes-catalogus__kaart-naam">{kop?.cell(rij)}</span>
          </div>
          {/* De compacte kaart toont alleen de eerste twee feiten; anders is "compact" alleen maar
              smaller en niet korter. */}
          <dl className="fawkes-catalogus__kaart-feiten">
            {(weergave === "compact" ? feiten.slice(0, 2) : feiten).map((c) => (
              <div key={c.header}>
                <dt>{c.header}</dt>
                <dd>{c.cell(rij)}</dd>
              </div>
            ))}
          </dl>
          {acties.length > 0 && (
            <div className="fawkes-catalogus__kaart-voet">
              {acties.map((c, i) => <span key={i}>{c.cell(rij)}</span>)}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

/**
 * De tegelrij boven een lijst, opgebouwd uit de rijen zelf: elke waarde die voorkomt wordt een tegel
 * met zijn aantal, plus "Alle" ervoor. Wat de tegels tonen verschilt per scherm — diersoort bij
 * pension en crèche, het soort dienst bij de ambulance — maar de manier van kiezen is overal gelijk.
 */
export function soortTegelsUitRijen<T>(
  rijen: T[],
  waarde: (rij: T) => string | null | undefined,
  alleLabel: string,
  icoon: (waarde: string) => ReactNode,
): SoortTegel[] {
  const telling = new Map<string, number>();
  for (const rij of rijen) {
    const v = (waarde(rij) ?? "").trim();
    if (v) telling.set(v, (telling.get(v) ?? 0) + 1);
  }
  // Eén waarde is geen indeling: dan zou "Alle" en die ene tegel allebei de hele lijst bevatten.
  // Geen tegels dus — het scherm laat de rij dan weg.
  if (telling.size < 2) return [];
  // Aflopend op aantal, en het totaal achteraan — zoals het dierenoverzicht het toont. Waar je
  // het meest van hebt staat vooraan; het totaal is geen soort en hoort daar niet tussen.
  return [
    ...[...telling.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "nl"))
      .map(([v, n]) => ({ key: v, label: v, aantal: n, icon: icoon(v) })),
    { key: "", label: alleLabel, aantal: rijen.length, icon: icoon("") },
  ];
}

export { WeergaveKeuze, SoortTegels };
export type { Weergave, SoortTegel };
